/* =========================================================
   Black Friday campaign runtime
   - Campaign dates and before / during / after states
   - Countdown timers (hero, cards, sticky bar, header bar)
   - Course cards: variant switching, price + discount badges,
     real seats left, add to basket (+ optional bonus product)
   - Live enrolments from real order data (Shopify Flow)
   - Click-to-load video, testimonial carousel, review read more
   ========================================================= */
(() => {
  'use strict';
  if (window.BFCampaign && window.BFCampaign.__ready) return;

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const pad = (n) => String(n).padStart(2, '0');

  /* ---------- Dates ---------- */
  // Accepts "2026-11-27 00:00", "2026-11-27T00:00", "2026-11-27" plus an offset like "+00:00"
  function parseDate(str, offset) {
    if (!str) return null;
    let s = String(str).trim();
    if (!s) return null;
    s = s.replace(/\s+/, 'T');
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) s += 'T00:00';
    if (/T\d{1,2}:\d{2}$/.test(s)) s += ':00';
    s = s.replace(/T(\d):/, 'T0$1:');
    if (!/(Z|[+-]\d{2}:?\d{2})$/i.test(s)) s += offset || '+00:00';
    const t = Date.parse(s);
    return Number.isNaN(t) ? null : t;
  }
  const stateFor = (now, start, end) => {
    if (start && now < start) return 'pre';
    if (end && now >= end) return 'ended';
    return 'live';
  };
  const parts = (ms) => {
    const total = Math.max(0, Math.floor(ms / 1000));
    return { d: Math.floor(total / 86400), h: Math.floor((total % 86400) / 3600), m: Math.floor((total % 3600) / 60), s: total % 60 };
  };
  const fmtText = (p) => (p.d > 0 ? p.d + 'd ' : '') + pad(p.h) + 'h ' + pad(p.m) + 'm ' + pad(p.s) + 's';

  /* ---------- Money ---------- */
  function formatMoney(cents, fmt) {
    fmt = fmt || '£{{amount}}';
    const match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    const key = match ? match[1] : 'amount';
    const value = Number(cents || 0) / 100;
    const delimit = (n, dec, th, ds) => {
      const [i, f] = n.toFixed(dec).split('.');
      const int = i.replace(/\B(?=(\d{3})+(?!\d))/g, th);
      return f ? int + ds + f : int;
    };
    let out;
    switch (key) {
      case 'amount_no_decimals': out = delimit(value, 0, ',', '.'); break;
      case 'amount_with_comma_separator': out = delimit(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': out = delimit(value, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': out = delimit(value, 2, "'", '.'); break;
      default: out = delimit(value, 2, ',', '.');
    }
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, out);
  }
  const shortMoney = (cents, fmt) => formatMoney(cents, fmt).replace(/[.,]00(?!\d)/, '');

  const BF = (window.BFCampaign = { __ready: true, parseDate, formatMoney, camp: null, state: null });

  /* ---------- Campaign state ---------- */
  function readCampaign() {
    const el = document.querySelector('[data-bf-campaign]');
    BF.camp = el
      ? { start: parseDate(el.dataset.start, el.dataset.offset), end: parseDate(el.dataset.end, el.dataset.offset) }
      : null;
  }

  function applyState(force) {
    if (!BF.camp) return;
    const st = stateFor(Date.now(), BF.camp.start, BF.camp.end);
    if (st === BF.state && !force) return;
    BF.state = st;
    document.documentElement.setAttribute('data-bf-state', st);
    document.querySelectorAll('[data-bf-show]').forEach((el) => {
      el.hidden = !el.getAttribute('data-bf-show').split(/\s+/).includes(st);
    });
    document.dispatchEvent(new CustomEvent('bf:state', { detail: { state: st } }));
  }

  /* ---------- Timers ---------- */
  function timerTarget(t) {
    if (t.dataset.end) return parseDate(t.dataset.end, t.dataset.offset);
    if (!BF.camp) return null;
    return BF.state === 'pre' ? BF.camp.start : BF.camp.end;
  }
  function tickTimers(now) {
    document.querySelectorAll('[data-bf-timer]').forEach((t) => {
      const target = timerTarget(t);
      if (!target) return;
      const p = parts(target - now);
      if (t.dataset.format === 'text') {
        const txt = fmtText(p);
        if (t.textContent !== txt) t.textContent = txt;
        return;
      }
      ['d', 'h', 'm', 's'].forEach((u) => {
        const n = t.querySelector('[data-u="' + u + '"]');
        const v = pad(p[u]);
        if (n && n.textContent !== v) n.textContent = v;
      });
    });
  }

  /* ---------- Site-wide header bar ---------- */
  const bars = [];
  function initBar(bar) {
    if (bar.__bf) return;
    bar.__bf = true;
    const start = parseDate(bar.dataset.start, bar.dataset.offset);
    const end = parseDate(bar.dataset.end, bar.dataset.offset);
    const teaser = bar.dataset.teaser === 'true';
    const prefix = bar.querySelector('[data-bf-bar-prefix]');
    const time = bar.querySelector('[data-bf-bar-time]');
    const update = (now) => {
      const st = stateFor(now, start, end);
      const show = st === 'live' || (st === 'pre' && teaser);
      bar.hidden = !show;
      if (!show) return;
      bar.querySelectorAll('[data-bf-bar-msg]').forEach((m) => { m.hidden = m.dataset.bfBarMsg !== st; });
      const target = st === 'pre' ? start : end;
      if (prefix) prefix.textContent = target ? (st === 'pre' ? prefix.dataset.pre : prefix.dataset.live) : '';
      if (time) time.textContent = target ? fmtText(parts(target - now)) : '';
    };
    bars.push(update);
    update(Date.now());
  }

  /* ---------- Course cards ---------- */
  const addItems = (items) =>
    fetch(ROOT + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items })
    });

  async function afterAdd(mode, productId) {
    if (mode === 'checkout') { window.location.href = ROOT + 'checkout'; return; }
    if (mode === 'cart') { window.location.href = ROOT + 'cart'; return; }

    // Drawer: tell Horizon the cart changed, then open its drawer. Falls back to the cart page.
    let cart = null;
    try { cart = await (await fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } })).json(); } catch (e) { /* ignore */ }
    try {
      const mod = await import('@theme/events');
      if (mod && mod.CartAddEvent) {
        document.dispatchEvent(
          new mod.CartAddEvent({}, 'bf-landing', {
            source: 'bf-landing',
            itemCount: cart ? cart.item_count : 0,
            productId: String(productId || '')
          })
        );
      }
    } catch (e) { /* theme without @theme/events: fall through */ }

    const drawer = document.querySelector('cart-drawer-component');
    let opened = false;
    if (drawer) {
      try {
        if (typeof drawer.open === 'function') { drawer.open(); opened = true; }
        else if (typeof drawer.showDialog === 'function') { drawer.showDialog(); opened = true; }
      } catch (e) { opened = false; }
    }
    if (!opened) window.location.href = ROOT + 'cart';
  }

  function initCourse(card) {
    if (card.__bf) return;
    card.__bf = true;

    let variants = [];
    try { variants = JSON.parse(card.querySelector('[data-bf-variants]').textContent); } catch (e) { variants = []; }
    const form = card.querySelector('form[data-bf-form]');
    if (!form) return;
    const select = form.querySelector('[data-bf-variant-select]');
    const idInput = select || form.querySelector('[name="id"]');
    const fmt = card.dataset.moneyFormat;
    const q = (sel) => card.querySelector(sel);
    const el = {
      price: q('[data-price]'), compare: q('[data-compare]'), save: q('[data-save]'),
      badge: q('[data-badge]'), badgeText: q('[data-badge-text]'), seats: q('[data-seats]'),
      dateText: q('[data-date-text]'), dateLabel: q('[data-date-label]'), dateLoc: q('[data-date-loc]'),
      atc: q('[data-atc]'), atcLabel: q('[data-atc-label]'), error: q('[data-error]')
    };
    const mode = card.dataset.badgeMode;
    const threshold = parseInt(card.dataset.seatsThreshold || '0', 10);
    const dateIdx = parseInt(card.dataset.dateIdx || '-1', 10);
    let touched = false;

    const current = () => {
      const id = Number(idInput && idInput.value);
      return variants.find((v) => v.id === id) || variants[0];
    };
    const dateFor = (v) => {
      if (v.date) {
        const [y, m, d] = String(v.date).split('-').map(Number);
        if (y && m && d) {
          return new Date(y, m - 1, d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });
        }
      }
      if (dateIdx >= 0 && v.opts && v.opts[dateIdx]) return v.opts[dateIdx];
      return v.title;
    };

    function render() {
      const v = current();
      if (!v) return;
      const disc = v.compare > v.price;
      const diff = v.compare - v.price;

      if (el.price) {
        el.price.textContent = formatMoney(v.price, fmt);
        el.price.classList.toggle('is-regular', !disc);
      }
      if (el.compare) {
        el.compare.hidden = !disc;
        if (disc) el.compare.textContent = formatMoney(v.compare, fmt);
      }
      if (el.save) {
        el.save.hidden = !disc;
        if (disc) el.save.textContent = (card.dataset.savePrefix || 'Save') + ' ' + shortMoney(diff, fmt);
      }
      if (el.badge && (mode === 'percent' || mode === 'amount')) {
        el.badge.hidden = !disc;
        if (disc && el.badgeText) {
          el.badgeText.textContent = mode === 'percent'
            ? Math.round((diff * 100) / v.compare) + '% OFF'
            : shortMoney(diff, fmt) + ' OFF';
        }
      }
      if (el.seats) {
        const low = v.available && v.managed && v.inv > 0 && v.inv <= threshold;
        el.seats.hidden = !low;
        if (low) {
          const tpl = v.inv === 1 ? card.dataset.seatText : card.dataset.seatsText;
          el.seats.textContent = (tpl || 'Only {n} spaces left').replace('{n}', v.inv);
        }
      }
      if (touched && el.dateText) {
        el.dateText.textContent = dateFor(v);
        if (el.dateLabel && card.dataset.selectedLabel) el.dateLabel.textContent = card.dataset.selectedLabel;
        if (el.dateLoc) {
          const loc = v.loc || card.dataset.defaultLocation || '';
          el.dateLoc.textContent = loc ? ' · ' + loc : '';
        }
      }
      if (el.atc) {
        el.atc.disabled = !v.available;
        if (el.atcLabel) el.atcLabel.textContent = v.available ? card.dataset.btnLabel : card.dataset.soldoutLabel;
      }
    }

    if (select) select.addEventListener('change', () => { touched = true; render(); });

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const v = current();
      if (!v || !v.available) return;
      if (el.error) el.error.hidden = true;
      if (el.atc) el.atc.classList.add('is-loading');

      const items = [{ id: v.id, quantity: 1 }];
      const bonusId = Number(card.dataset.bonusVariant || 0);
      const bonusLive = !BF.camp || BF.state === 'live';
      if (bonusId && bonusLive) items.push({ id: bonusId, quantity: 1, properties: { _bf_bonus: 'Black Friday bonus' } });

      try {
        let res = await addItems(items);
        if (!res.ok && items.length > 1) res = await addItems([items[0]]); // bonus unavailable: still sell the course
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.description || data.message || 'Sorry, that could not be added to your basket.');
        }
        await afterAdd(card.dataset.afterAdd, card.dataset.productId);
      } catch (err) {
        if (el.error) { el.error.textContent = err.message; el.error.hidden = false; }
      } finally {
        if (el.atc) el.atc.classList.remove('is-loading');
      }
    });

    render();
  }

  /* ---------- Sticky mobile bar ---------- */
  function initSticky(root) {
    if (!('IntersectionObserver' in window)) return;
    root.querySelectorAll('[data-bf-sticky]').forEach((sticky) => {
      if (sticky.__bf) return;
      sticky.__bf = true;
      const hero = sticky.closest('[data-bf-hero]');
      const courses = document.querySelector('[data-bf-courses]');
      let heroOut = false;
      let coursesIn = false;
      const sync = () => sticky.classList.toggle('is-visible', heroOut && !coursesIn);
      if (hero) {
        new IntersectionObserver(([e]) => { heroOut = !e.isIntersecting; sync(); })
          .observe(hero.querySelector('.bf-hero__grid') || hero);
      }
      if (courses) {
        new IntersectionObserver(([e]) => { coursesIn = e.isIntersecting; sync(); }, { rootMargin: '0px 0px -35% 0px' })
          .observe(courses);
      }
    });
  }

  /* ---------- Live enrolments (real orders via Shopify Flow) ---------- */
  function initProof(box) {
    if (box.__bf) return;
    box.__bf = true;
    let d = null;
    try {
      d = JSON.parse(box.querySelector('[data-bf-proof-data]').textContent);
      if (typeof d === 'string') d = JSON.parse(d);
    } catch (e) { d = null; }

    const section = box.closest('.bf-section');
    const designMode = !!(window.Shopify && window.Shopify.designMode);
    const now = Date.now();
    const updated = d && d.updated ? Date.parse(d.updated) : NaN;
    const fresh = !Number.isNaN(updated) && now - updated <= Number(box.dataset.stale || 3) * 3600e3;

    let showing = false;
    const counter = box.querySelector('[data-bf-proof-counter]');
    const count = fresh ? Number(d.count) || 0 : 0;
    if (counter && count > 0 && count >= Number(box.dataset.min || 0)) {
      counter.querySelector('[data-bf-proof-text]').textContent = (box.dataset.text || '[count]').replace(/\[count\]|\{count\}/g, count);
      counter.hidden = false;
      showing = true;
    }

    const toastsOn = box.dataset.toasts === 'true' && fresh && Array.isArray(d.recent) && d.recent.length > 0;
    if (toastsOn) startToasts(box, d.recent);
    if (!showing && !designMode && section) section.hidden = true;
  }

  function startToasts(box, recent) {
    const toast = box.querySelector('[data-bf-toast]');
    if (!toast) return;
    document.body.appendChild(toast);

    const maxAge = Number(box.dataset.maxAge || 24) * 3600e3;
    const KEY = 'bf_toasts_seen';
    let seen = [];
    try { seen = JSON.parse(sessionStorage.getItem(KEY) || '[]'); } catch (e) { seen = []; }
    const list = recent.filter((r) => r && r.at && Date.now() - Date.parse(r.at) <= maxAge && !seen.includes(r.at));
    if (!list.length) return;

    const max = Number(box.dataset.maxToasts || 4);
    const interval = Number(box.dataset.interval || 20) * 1000;
    const names = box.dataset.names === 'true';
    const anon = box.dataset.anon || 'Someone';
    const tpl = box.dataset.toastText || '{who} enrolled on {product}';
    const textEl = toast.querySelector('[data-bf-toast-text]');
    const timeEl = toast.querySelector('[data-bf-toast-time]');
    let i = 0;
    let shownThisPage = 0;
    let closed = false;
    let timer;

    const ago = (ms) => {
      const m = Math.round(ms / 60000);
      if (m < 2) return 'Just now';
      if (m < 60) return m + ' minutes ago';
      const h = Math.round(m / 60);
      return h === 1 ? '1 hour ago' : h + ' hours ago';
    };

    const show = () => {
      if (closed || i >= list.length || seen.length >= max || shownThisPage >= max) return;
      const r = list[i++];
      const who = (names && r.n ? r.n : anon) + (r.t ? ' in ' + r.t : '');
      textEl.textContent = tpl.replace('{who}', who).replace('{product}', r.p || 'a course');
      timeEl.textContent = ago(Date.now() - Date.parse(r.at));
      toast.hidden = false;
      requestAnimationFrame(() => toast.classList.add('is-in'));
      seen.push(r.at);
      shownThisPage++;
      try { sessionStorage.setItem(KEY, JSON.stringify(seen)); } catch (e) { /* ignore */ }
      setTimeout(() => {
        toast.classList.remove('is-in');
        setTimeout(() => { toast.hidden = true; }, 400);
      }, 6000);
      timer = setTimeout(show, interval);
    };

    const close = toast.querySelector('[data-bf-toast-close]');
    if (close) {
      close.addEventListener('click', () => {
        closed = true;
        clearTimeout(timer);
        toast.classList.remove('is-in');
        toast.hidden = true;
      });
    }
    timer = setTimeout(show, Number(box.dataset.firstDelay || 6) * 1000);
  }

  /* ---------- Reviews read more ---------- */
  /* ---------- Online / in-person switcher ---------- */
  // Keeps the switcher below the theme's sticky header, highlights the group in view
  const HEADER_SEL = 'header-component, #header-component, .header-section, #shopify-section-header, sticky-header, .shopify-section-group-header-group, header';
  function headerBottom() {
    let bottom = 0;
    document.querySelectorAll(HEADER_SEL).forEach((el) => {
      let node = el;
      let pinned = false;
      for (let i = 0; i < 4 && node && node !== document.body; i++) {
        const pos = getComputedStyle(node).position;
        if (pos === 'sticky' || pos === 'fixed') { pinned = true; break; }
        node = node.parentElement;
      }
      if (!pinned) return;
      const r = el.getBoundingClientRect();
      if (r.height > 0 && r.top <= 1 && r.bottom > bottom && r.bottom < window.innerHeight / 2) bottom = r.bottom;
    });
    return Math.max(0, Math.round(bottom));
  }

  let spyTicking = false;
  function updateSwitch() {
    spyTicking = false;
    const sw = document.querySelector('[data-bf-switch]');
    if (!sw) return;
    const top = headerBottom();
    document.documentElement.style.setProperty('--bf-sticky-top', top + 'px');
    const swRect = sw.getBoundingClientRect();
    sw.classList.toggle('is-stuck', swRect.top <= top + 1);

    if (isTabs()) return; // tabs mode: the active tab is set by clicks, not scroll position

    const line = top + swRect.height + 40;
    const groups = [...document.querySelectorAll('[data-bf-group]')];
    let active = groups[0];
    groups.forEach((g) => { if (g.getBoundingClientRect().top <= line) active = g; });
    if (!active) return;
    sw.querySelectorAll('[data-bf-tab]').forEach((t) => {
      const on = t.dataset.bfTab === active.dataset.bfGroup;
      t.classList.toggle('is-active', on);
      if (on) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current');
    });
  }
  const requestSwitch = () => { if (!spyTicking) { spyTicking = true; requestAnimationFrame(updateSwitch); } };
  window.addEventListener('scroll', requestSwitch, { passive: true });
  window.addEventListener('resize', requestSwitch);

  // Tabs mode: show one group at a time
  const isTabs = () => !!document.querySelector('[data-bf-courses][data-bf-tabs]');
  function activateGroup(name, focusTab) {
    const courses = document.querySelector('[data-bf-courses][data-bf-tabs]');
    if (!courses) return false;
    const target = courses.querySelector('[data-bf-group="' + name + '"]');
    if (!target) return false;
    courses.querySelectorAll('[data-bf-group]').forEach((g) => {
      const on = g === target;
      const was = !g.classList.contains('is-hidden-tab');
      g.classList.toggle('is-hidden-tab', !on);
      if (on && !was) {
        g.classList.remove('is-entering');
        void g.offsetWidth;
        g.classList.add('is-entering');
      }
    });
    courses.querySelectorAll('[data-bf-tab]').forEach((t) => {
      const on = t.dataset.bfTab === name;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.setAttribute('tabindex', on ? '0' : '-1');
      if (on && focusTab) t.focus();
    });
    fitTracks();
    return true;
  }

  function scrollToEl(el) {
    const sw = document.querySelector('[data-bf-switch]');
    const offset = headerBottom() + (sw ? sw.offsetHeight : 0) + 12;
    const y = el.getBoundingClientRect().top + window.scrollY - offset;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: Math.max(0, y), behavior: reduce ? 'auto' : 'smooth' });
  }

  // Hero buttons, offer lines and switcher tabs
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[data-bf-jump]');
    if (!link) return;
    const id = (link.getAttribute('href') || '').slice(1);
    const target = id && document.getElementById(id);
    if (!target) return;
    e.preventDefault();
    updateSwitch();

    const fromSwitch = !!link.closest('[data-bf-switch]');
    if (isTabs() && target.hasAttribute('data-bf-group')) {
      activateGroup(target.dataset.bfGroup);
      // From the tabs: only scroll if the top of the new group is out of view
      const sw = document.querySelector('[data-bf-switch]');
      const line = headerBottom() + (sw ? sw.offsetHeight : 0);
      if (!fromSwitch || target.getBoundingClientRect().top < line) scrollToEl(target);
    } else {
      scrollToEl(target);
    }
    try { history.replaceState(null, '', '#' + id); } catch (err) { /* ignore */ }
  });

  // Keyboard: left / right arrows move between tabs
  document.addEventListener('keydown', (e) => {
    const tab = e.target.closest && e.target.closest('[data-bf-tabs] [role="tab"]');
    if (!tab || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
    const tabs = [...tab.parentElement.querySelectorAll('[role="tab"]')];
    const i = tabs.indexOf(tab);
    const next = tabs[(i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
    e.preventDefault();
    activateGroup(next.dataset.bfTab, true);
  });

  // Theme editor: selecting a course block opens its tab
  document.addEventListener('shopify:block:select', (e) => {
    const g = e.target.closest && e.target.closest('[data-bf-group]');
    if (g) activateGroup(g.dataset.bfGroup);
  });

  // Opening the page with #bf-group-in_person (e.g. from an email) opens that tab
  function openFromHash() {
    const m = (window.location.hash || '').match(/^#bf-group-(\w+)$/);
    if (m && activateGroup(m[1])) {
      const el = document.getElementById('bf-group-' + m[1]);
      if (el) setTimeout(() => scrollToEl(el), 50);
    }
  }

  /* ---------- Carousels: centre when everything fits, hide arrows ---------- */
  function fitTracks() {
    document.querySelectorAll('[data-bf-track]').forEach((track) => {
      track.classList.remove('is-fit');
      const fits = track.scrollWidth <= track.clientWidth + 2;
      track.classList.toggle('is-fit', fits);
      const nav = track.parentElement.querySelector('.bf-nav');
      if (nav) nav.classList.toggle('is-hidden', fits);
    });
  }
  let fitTimer;
  window.addEventListener('resize', () => { clearTimeout(fitTimer); fitTimer = setTimeout(fitTracks, 150); });

  function initReviews(root) {
    root.querySelectorAll('[data-bf-rv-text]').forEach((t) => {
      if (t.__bf) return;
      t.__bf = true;
      const btn = t.parentElement.querySelector('[data-bf-rv-more]');
      if (!btn || t.scrollHeight <= t.clientHeight + 2) return;
      btn.hidden = false;
      btn.addEventListener('click', () => {
        const open = t.classList.toggle('is-open');
        btn.textContent = open ? btn.dataset.less : btn.dataset.more;
      });
    });
  }

  /* ---------- Delegated clicks ---------- */
  document.addEventListener('click', (e) => {
    const lite = e.target.closest('[data-bf-lite]');
    if (lite) {
      const id = lite.dataset.id;
      const src = lite.dataset.type === 'vimeo'
        ? 'https://player.vimeo.com/video/' + id + '?autoplay=1'
        : 'https://www.youtube-nocookie.com/embed/' + id + '?autoplay=1&rel=0&playsinline=1';
      const iframe = document.createElement('iframe');
      iframe.src = src;
      iframe.className = 'bf-lite__frame';
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      iframe.allowFullscreen = true;
      iframe.title = lite.getAttribute('aria-label') || 'Video';
      lite.replaceWith(iframe);
      return;
    }

    const unmute = e.target.closest('[data-bf-unmute]');
    if (unmute) {
      const video = unmute.parentElement.querySelector('video');
      if (video) {
        video.muted = false;
        video.controls = true;
        video.currentTime = 0;
        video.play().catch(() => {});
      }
      unmute.remove();
      return;
    }

    const nav = e.target.closest('[data-bf-prev], [data-bf-next]');
    if (nav) {
      const track = nav.closest('.bf-section').querySelector('[data-bf-track]');
      if (!track) return;
      const item = track.firstElementChild;
      const step = item ? item.getBoundingClientRect().width + 16 : track.clientWidth * 0.8;
      track.scrollBy({ left: nav.hasAttribute('data-bf-prev') ? -step : step, behavior: 'smooth' });
    }
  });

  // Only one testimonial video plays at a time
  document.addEventListener('play', (e) => {
    const t = e.target;
    if (!(t instanceof HTMLVideoElement) || !t.closest('.bf-vt')) return;
    document.querySelectorAll('.bf-vt video').forEach((v) => { if (v !== t) v.pause(); });
  }, true);

  /* ---------- Boot ---------- */
  function initAll(root) {
    root = root || document;
    readCampaign();
    root.querySelectorAll('[data-bf-bar]').forEach(initBar);
    root.querySelectorAll('[data-bf-course]').forEach(initCourse);
    root.querySelectorAll('[data-bf-proof]').forEach(initProof);
    initSticky(root);
    initReviews(root);
    fitTracks();
    updateSwitch();
    openFromHash();
    applyState(true);
    tickTimers(Date.now());
  }

  initAll(document);
  setInterval(() => {
    const now = Date.now();
    applyState(false);
    tickTimers(now);
    bars.forEach((update) => update(now));
  }, 1000);

  // Theme editor: re-initialise sections when they are added or edited
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
})();